import * as THREE from 'three';
import { fbm3d } from '../../visual';

/**
 * Geometry cache for ore deposit visuals — uranium halos and ore nodules.
 *
 * Each individual asteroid used to build its own SphereGeometry per halo and
 * a fresh IcosahedronGeometry per nodule. With ~175 uranium and ~50 gold
 * asteroids carrying multiple deposits each, that adds up to ~1.5k unique
 * geometries on disk + VRAM for shapes that look almost identical.
 *
 * We pre-build a small pool of templates and share them across instances.
 * Per-instance variety is preserved through random rotation, scale, and
 * material — the eye reads each cluster as distinct without spending VRAM
 * on uniqueness no one can perceive at runtime.
 *
 * The cached geometries are unit-radius — callers scale via mesh.scale so a
 * single shared shape covers any deposit size.
 */

const NODULE_TEMPLATE_COUNT = 12;
let _haloGeometry: THREE.SphereGeometry | null = null;
let _noduleTemplates: THREE.BufferGeometry[] | null = null;

/** Unit-radius sphere shared by every uranium halo in the sector. Halos are
 *  additive-blended billboards-of-sorts where each instance differs only in
 *  scale and color, so a shared mesh-resolution sphere is visually identical
 *  to the per-instance copies it replaces. */
export function getSharedHaloGeometry(): THREE.SphereGeometry {
  if (!_haloGeometry) {
    _haloGeometry = new THREE.SphereGeometry(1, 14, 12);
  }
  return _haloGeometry;
}

/** Pick a pre-built unit-radius nodule geometry from the shared pool.
 *  The 12 templates each use a different FBM seed, so a cluster of nodules
 *  reads as a varied lump even though the underlying buffers are reused. */
export function getSharedNoduleGeometry(): THREE.BufferGeometry {
  if (!_noduleTemplates) {
    _noduleTemplates = [];
    for (let i = 0; i < NODULE_TEMPLATE_COUNT; i++) {
      _noduleTemplates.push(buildNoduleTemplate());
    }
  }
  const list = _noduleTemplates;
  return list[Math.floor(Math.random() * list.length)]!;
}

function buildNoduleTemplate(): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(1, 1);
  const position = geometry.attributes['position'] as THREE.BufferAttribute;

  const seedX = Math.random() * 40;
  const seedY = Math.random() * 40;
  const seedZ = Math.random() * 40;
  const axis = new THREE.Vector3(
    0.78 + Math.random() * 0.34,
    0.78 + Math.random() * 0.34,
    0.78 + Math.random() * 0.34
  );

  const dir = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    dir.set(position.getX(i), position.getY(i), position.getZ(i)).normalize();
    const macro = fbm3d(
      dir.x * 1.7 + seedX,
      dir.y * 1.7 + seedY,
      dir.z * 1.7 + seedZ,
      3
    );
    const detail = fbm3d(
      dir.x * 4.4 + seedX,
      dir.y * 4.4 + seedY,
      dir.z * 4.4 + seedZ,
      2
    );
    const d = Math.max(0.6, 1 + macro * 0.3 + detail * 0.12);
    position.setXYZ(
      i,
      dir.x * axis.x * d,
      dir.y * axis.y * d,
      dir.z * axis.z * d
    );
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}
